// ./src/components/features/Logbook/LogbookEditDrawer/sections/SharedSections.tsx
//
// Small collapsible sections shared by all logbook record types:
// Travel, Tags, Comments, Documents.
// Grouped in one file since each is small and always used together.

import React from "react";
import { Textarea, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FormData } from "../../types";

import { UploadedFile } from "@/utils/api/UploadedFileService";

import { CollapsibleSection } from "@/components/generic/CollapsibleSection";
import { AttachedFiles } from "@/components/generic/AttachedFiles";
import { TagsSelectorInput } from "@/components/generic/TagsSelectorInput";

import { TravelSelector } from "@/components/features/Travels/TravelSelector";

// ============================================
// TravelSection
// ============================================

export interface TravelSectionProps {
  formData: FormData;
  expanded: boolean;
  onToggle: () => void;
  onFieldChange: (field: string, value: string | number | boolean | string[] | null) => void;
  isEditMode: boolean;
}

const TravelSectionComponent: React.FC<TravelSectionProps> = ({
  formData,
  expanded,
  onToggle,
  onFieldChange,
  isEditMode,
}) => {
  const { t } = useTranslation(["common"]);

  return (
    <CollapsibleSection
      title={t("common:form.sections.travel")}
      expanded={expanded}
      onToggle={onToggle}
      optional
      withDivider
    >
      <TravelSelector
        name="travelId"
        value={formData.travelId}
        onChange={onFieldChange}
        carId={formData.carId}
        preselectActiveTravel={!isEditMode}
        label={t("common:selectors.travel.label")}
        placeholder={t("common:selectors.travel.placeholder")}
        size="md"
      />
      <Text size="xs" c="dimmed">
        {t("common:selectors.travel.hint")}
      </Text>
    </CollapsibleSection>
  );
};

export const TravelSection = React.memo(TravelSectionComponent);

// ============================================
// TagsSection
// ============================================

export interface TagsSectionProps {
  formData: FormData;
  expanded: boolean;
  onToggle: () => void;
  onFieldChange: (field: string, value: string | number | boolean | string[] | null) => void;
}

const TagsSectionComponent: React.FC<TagsSectionProps> = ({
  formData,
  expanded,
  onToggle,
  onFieldChange,
}) => {
  const { t } = useTranslation(["logbook", "common"]);

  return (
    <CollapsibleSection
      title={t("logbook:form.sections.tags", { defaultValue: "Tags" })}
      expanded={expanded}
      onToggle={onToggle}
      optional
      badge={formData.tagIds.length}
      withDivider
    >
      <TagsSelectorInput
        name="tagIds"
        value={formData.tagIds}
        onChange={onFieldChange}
        label={t("logbook:form.fields.tags.label", { defaultValue: "Tags" })}
        placeholder={t("logbook:form.fields.tags.placeholder", {
          defaultValue: "Add tags to organize this record",
        })}
        maxTags={5}
        allowCreate
        size="md"
      />
    </CollapsibleSection>
  );
};

export const TagsSection = React.memo(TagsSectionComponent);

// ============================================
// CommentsSection
// ============================================

export interface CommentsSectionProps {
  formData: FormData;
  expanded: boolean;
  onToggle: () => void;
  onTextInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  apiFieldErrors: Record<string, string | undefined>;
}

const CommentsSectionComponent: React.FC<CommentsSectionProps> = ({
  formData,
  expanded,
  onToggle,
  onTextInputChange,
  apiFieldErrors,
}) => {
  const { t } = useTranslation(["logbook", "common"]);

  return (
    <CollapsibleSection
      title={t("logbook:form.sections.additional", { defaultValue: "Additional" })}
      expanded={expanded}
      onToggle={onToggle}
      optional
      withDivider
    >
      <Textarea
        label={t("logbook:form.fields.comments.label", { defaultValue: "Comments" })}
        placeholder={t("logbook:form.fields.comments.placeholder", {
          defaultValue: "Add notes or comments",
        })}
        name="comments"
        value={formData.comments}
        onChange={onTextInputChange}
        error={apiFieldErrors.comments}
        minRows={3}
        maxRows={6}
        autosize
        size="md"
      />
    </CollapsibleSection>
  );
};

export const CommentsSection = React.memo(CommentsSectionComponent);

// ============================================
// DocumentsSection
// ============================================

export interface DocumentsSectionProps {
  formData: FormData;
  expanded: boolean;
  onToggle: () => void;
  attachedFiles: UploadedFile[];
  onRemoveFile: (fileId: string) => void;
  onAddClick: () => void;
  onReorderFiles: (reorderedFiles: UploadedFile[]) => void;
}

const DocumentsSectionComponent: React.FC<DocumentsSectionProps> = ({
  //formData,
  expanded,
  onToggle,
  attachedFiles,
  onRemoveFile,
  onAddClick,
  onReorderFiles,
}) => {
  const { t } = useTranslation(["logbook", "common"]);

  return (
    <CollapsibleSection
      title={t("logbook:form.sections.documents", { defaultValue: "Documents" })}
      expanded={expanded}
      onToggle={onToggle}
      optional
      badge={attachedFiles.length}
      withDivider
    >
      <AttachedFiles
        files={attachedFiles}
        onRemove={onRemoveFile}
        onAddClick={onAddClick}
        onReorder={onReorderFiles}
        reorderable={attachedFiles.length > 1}
        addButtonLabel={t("common:attachments.addDocument")}
        emptyMessage={t("common:attachments.noDocuments")}
        cardSize="sm"
        gridColumns={{ base: 2, sm: 3, md: 4 }}
      />
    </CollapsibleSection>
  );
};

export const DocumentsSection = React.memo(DocumentsSectionComponent);